import {ExcelCommonHelper} from './excel-common.helper';
import {Borders, Fill, Font} from 'exceljs';

/**
 * Excel Table Column
 */
export interface ExcelTableColumn {
	/** {string} property */
	property: string;
	/** {string} title */
	title: string;
	/** {'left' | 'right' | 'center' | 'fill' | 'justify' | 'centerContinuous' | 'distributed'} excelAlign */
	excelAlign?: 'left' | 'right' | 'center' | 'fill' | 'justify' | 'centerContinuous' | 'distributed';
	/** {number} customWidth */
	customWidth?: number;
	/** {number} height */
	height?: number;
	/** {Partial<Font>} customFont */
	customFont?: Partial<Font>;
	/** {Fill} customFill */
	customFill?: Fill;
	/** {Partial<Borders>} customBorder */
	customBorder?: Partial<Borders>;
	/** {boolean} formatter */
	formatter?: boolean;
	/** {boolean} mergeWithPrev */
	mergeWithPrev?: boolean;
	/** {string | boolean} numFmt */
	numFmt?: string | boolean;
	/** {boolean} isDealColumn */
	isDealColumn?: boolean;
}

export type ExcelTableRow =
	| {
			isTotal?: boolean;
			isSubHeader?: boolean;
			isColumnHeader?: boolean;
			isInlineHeader?: boolean;
			title?: string;
			isEmptyRow?: boolean;
			/** {Partial<Font>} customFont */
			customFont?: Partial<Font>;
			/** {Fill} customFill */
			customFill?: Fill;
			/** {Partial<Borders>} customBorder */
			customBorder?: Partial<Borders>;
	  }
	| Record<string, unknown>;

/**
 * Excel Table (can be several on the TAB)
 */
export class ExcelTable {
	/** Auxiliary 'customColumnHeader' variable. */
	public customColumnHeader = false;
	/** Auxiliary 'columnsConfig' variable. */
	public columnsConfig: ExcelTableColumn[] = null;
	/** Auxiliary 'columnsNumber' variable. */
	public columnsNumber = 0;

	/**
	 * Constructor for ExcelTable class.
	 * @constructor
	 * @param {ExcelTableRow[]} rows rows
	 * @param {ExcelTableColumn[]} columnsConfigDraft columns config draft
	 * @param {any[]} totals totals
	 */
	constructor(public rows: ExcelTableRow[], columnsConfigDraft?: ExcelTableColumn[], public totals?: any[]) {
		this.customColumnHeader = rows.some((row) => row?.isColumnHeader);
		if (columnsConfigDraft) {
			const dataRow = rows.find((row) => !!row && !row.isColumnHeader && !row.isSubHeader && !row.isInlineHeader);
			this.columnsConfig = ExcelCommonHelper.getExcelTableConfig(dataRow, columnsConfigDraft);
			this.columnsNumber = this.columnsConfig?.length || 0;
		}
	}

	/**
	 * Check is no data.
	 * @returns {boolean}
	 */
	public get isNoData(): boolean {
		return !this.rows.length;
	}
}

/**
 * Excel Tab (Sheet)
 * consist one or more Excel Tables
 */
export class ExcelTab {
	/** Auxiliary 'widthInCells' variable. */
	public widthInCells = 1;

	/**
	 * Constructor for ExcelTab class.
	 * @constructor
	 * @param {ExcelTable[]} tables tables
	 * @param {ExcelTableColumn[]} columns columns
	 * @param {string} title title
	 * @param {string} label label
	 */
	constructor(
		public tables: ExcelTable[],
		public columns: ExcelTableColumn[],
		public title: string,
		public label: string
	) {
		const widths = tables.map((table) => table.columnsNumber);
		this.widthInCells = Math.max(...widths);
	}

	/**
	 * Check is no data.
	 * @returns {boolean}
	 */
	public get isNoData(): boolean {
		return !this.tables.length;
	}

	/**
	 * Set width in cells.
	 * @param {number} number
	 * @returns {void}
	 */
	public setWidthInCells(number: number): void {
		this.widthInCells = +number || 1;
	}
}
